import {
  computed, onMounted, onBeforeUnmount, ref, watch, type Ref,
} from 'vue';
import { useZIndexManager } from '@apparatix/stores/useZIndexManager';
import { useStringFunctions } from './useStringFunctions';
import type { ElevationCategory } from '@apparatix/constants/Elevation';

type UseManagedZIndexOptions = {
  /**
   * 'onMounted' sets the z-index once when the component/element is mounted. 'watch` does NOT set the index when mounted, and instead adds a watch to the provided element.
   * 'onMounted' is preferred for the slight performance benefit, but won't work in cases where there isn't an available mount/unmount cycle, ie a case like
   * <div v-if="someCondition" ref="divThatNeedsAProvidedIndex" />
   * For the best of both worlds, you can use library/ElevationManagedDiv.vue instead of a raw div. That component provides a lightweight wrapper around a div so that when
   * a v-if is toggled, it will fire the onMounted and onBeforeUnmount lifecycle hooks
   */
  strategy?: 'onMounted' | 'watch',
  /**
   * Unused when in onMounted mode. When in watch mode, this determines whether the watch immediately fires, ie watch(() => { ... }, { immediate: true })
   */
  watchImmediate?: boolean,
};

const defaultOptions: UseManagedZIndexOptions = {
  strategy: 'onMounted',
  watchImmediate: false,
};

/**
 * This taps into the z-index management system that Primevue uses internally, and allows the z-index of the provided element to be dynamically allocated
 * depending on what other component[s] are currently visible on the screen. This generally would only be needed when you're having issues with your
 * component appearing behind a stock PrimeVue component
 * @param el The element you want to set the z-index on
 * @param category The UI category of the element. When in doubt, just use 'modal'
 */
export const useManagedZIndex = (el: Ref<HTMLElement>, category: ElevationCategory = 'modal', options: UseManagedZIndexOptions = defaultOptions) => {
  const indexManager = useZIndexManager();
  const { isEmptyString } = useStringFunctions();

  const managerTicket = ref<string>();

  const currentIndex = computed<number>(() => {
    if (isEmptyString(el.value?.style?.zIndex, true)) return undefined;

    return parseInt(el.value.style.zIndex, 10);
  });

  const setElementIndex = () => {
    if (!el?.value) return;

    const [ticket, zIndex] = indexManager.allocateIndex(category);

    managerTicket.value = ticket;
    el.value.style.zIndex = String(zIndex);
  };

  const clearElementIndex = () => {
    indexManager.releaseIndex(managerTicket.value);

    if (el.value) {
      el.value.style.zIndex = undefined;
    }
  };

  if (options.strategy === 'watch') {
    watch(() => el?.value, (newValue, oldValue) => {
      if (newValue) {
        setElementIndex();
      }

      if (!newValue && oldValue) {
        clearElementIndex();
      }
    }, { immediate: options.watchImmediate });
  }

  onMounted(() => {
    if (options.strategy !== 'onMounted') return;

    setElementIndex();
  });

  onBeforeUnmount(() => {
    if (options.strategy !== 'onMounted') return;

    clearElementIndex();
  });

  return { currentIndex };
};
